import React, { KeyboardEvent, useRef, useState } from 'react';

import Chip from '@/components/ui/chip';
import {
  type ValidationState,
  validationBorderClasses,
  validationRingClasses,
} from '@/theme/size-tokens';

export type TagInputProps = {
  /** Controlled list of tag values */
  value: string[];
  /** Called with the new full list whenever tags change */
  onChange: (tags: string[]) => void;
  /** Placeholder shown when there are no tags yet */
  placeholder?: string;
  /** Disables all interaction */
  disabled?: boolean;
  /** Visual validation state */
  validationState?: ValidationState;
  /** HTML id forwarded to the inner <input> (injected by FormField) */
  id?: string;
  /** aria-describedby forwarded from FormField */
  'aria-describedby'?: string;
  /**
   * Normalise each draft and committed tag. The default is slug-shaped
   * (lowercase, `[a-z0-9-]`), which is wrong for values such as email domains
   * or OAuth scopes that carry dots, slashes and case; pass a trimming
   * function for those.
   */
  sanitize?: (raw: string) => string;
};

function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[ _]/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function TagInput({
  value,
  onChange,
  placeholder,
  disabled = false,
  validationState = 'neutral',
  id,
  'aria-describedby': ariaDescribedBy,
  sanitize = slugify,
}: Readonly<TagInputProps>) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = (raw: string) => {
    const tag = sanitize(raw).replace(/^-+|-+$/g, '');
    if (tag && !value.includes(tag)) {
      onChange([...value, tag]);
    }
    setDraft('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Commit on comma typed
    if (raw.endsWith(',')) {
      commit(raw.slice(0, -1));
      return;
    }
    setDraft(sanitize(raw));
  };

  // Convert focus: → focus-within: so the container ring activates on inner focus
  const borderClass = validationBorderClasses[validationState].replace(
    /focus:/g,
    'focus-within:'
  );
  const ringClass = validationRingClasses[validationState].replace(
    /focus:/g,
    'focus-within:'
  );

  return (
    <div
      className={`bg-background flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border px-3 py-2 shadow-md transition-all duration-200 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-offset-0 focus-within:outline-none ${borderClass} ${ringClass} ${disabled ? 'bg-muted/50 cursor-not-allowed opacity-50' : 'cursor-text'} `}
      onClick={() => !disabled && inputRef.current?.focus()}
    >
      {value.map((tag) => (
        <Chip
          key={tag}
          size="sm"
          variant="primary"
          wrap
          className="max-w-full"
          onRemove={
            disabled
              ? undefined
              : () => onChange(value.filter((t) => t !== tag))
          }
        >
          {tag}
        </Chip>
      ))}

      <input
        ref={inputRef}
        id={id}
        aria-describedby={ariaDescribedBy}
        value={draft}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : undefined}
        disabled={disabled}
        autoComplete="off"
        className="placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent text-sm outline-none disabled:cursor-not-allowed"
      />
    </div>
  );
}

TagInput.displayName = 'TagInput';

export default TagInput;
